import classNames from 'classnames';
import { GridCell } from 'components/common-form/builder/form/node/field/Grid/GridCell';
import {
  buildCellId,
  clippingWithGrid,
  nextCellCoordinates,
} from 'components/common-form/builder/form/node/field/Grid/GridField.helper';
import {
  CellCoordinates,
  GridData,
  Navigation,
} from 'components/common-form/builder/form/node/field/Grid/GridField.model';
import { useTranslateForm } from 'hooks/use-translate-payoff';
import { usePasteGridData } from 'hooks/usePasteGridData';
import { GridWithoutPaginationMetadata } from 'models/forms/fields';
import { useCallback, useMemo, useState } from 'react';
import { useFormFieldData } from 'routes/essential-workspace/components/form/use-form-field-data';

type GridWithoutPaginationProps = GridWithoutPaginationMetadata & { className?: string };
export function GridWithoutPagination(props: GridWithoutPaginationProps) {
  const { id, headers, hasRowIndexColumn, valuesMetadata, className } = props;

  const [isEditing, setIsEditing] = useState(false);
  const { translateLabel } = useTranslateForm();
  const [gridData, setGridData] = useFormFieldData<GridData>(id, []);

  const baseHeaders = useMemo(() => headers.filter(header => !header.isHidden && !header.isForAdvancedMode), [headers]);

  const nbRows = gridData.length;
  const nbColumns = headers?.length ?? 0;
  const hasHeaders = nbColumns > 0;
  const hasData = nbRows > 0;

  const onPaste = usePasteGridData({ gridData, headers, setGridData });

  const [selectedCellIds, setSelectedCellIds] = useState<string[]>([]);
  const isSelected = useCallback(
    (cellId: string) => {
      return selectedCellIds.includes(cellId);
    },
    [selectedCellIds],
  );

  const onCellValueChanged = useCallback(
    (row: number, cellId: string, value: unknown) => {
      const newRow: Record<string, unknown> = { ...gridData[row], [cellId]: value, isEdited: true };
      const newGridData: GridData = [...gridData.slice(0, row), newRow, ...gridData.slice(row + 1)];
      setGridData(newGridData);
    },
    [gridData, setGridData],
  );

  const onCellSelected = useCallback((cellId: string) => {
    setSelectedCellIds(new Array(cellId));
  }, []);

  const onNavigated = useCallback(
    (origin: CellCoordinates, navigation: Navigation, newIsEditing: boolean = false) => {
      const clipping = clippingWithGrid({
        gridId: id,
        nbRows,
        nbColumns,
      });
      const nextCoordinates = nextCellCoordinates(origin, navigation, clipping);
      const nextCellId = buildCellId(id, nextCoordinates.column, nextCoordinates.row);

      setSelectedCellIds(new Array(nextCellId));
      setIsEditing(() => newIsEditing);
    },
    [id, nbColumns, nbRows],
  );

  if (!hasHeaders || !hasData) {
    return null;
  }

  const thClassname = 'text-secondary py-2 border-bottom-0';

  return (
    <table className={classNames('table table-bordered', className)}>
      <thead className="sticky-top bg-lvl1" style={{ top: -1 }}>
        <tr>
          <th className={thClassname}>{translateLabel(`${id}.shortPeriodNumber`)}</th>
          {baseHeaders.map(header => (
            <th key={header.id} className={thClassname}>
              {translateLabel(header.translationKey ?? header.id)}
            </th>
          ))}
        </tr>
      </thead>

      <tbody>
        {gridData.map((line, rowIndex) => {
          return (
            <tr key={rowIndex}>
              {hasRowIndexColumn && (
                <th scope="row" className="align-middle text-center text-secondary p-0">
                  #{rowIndex + 1}
                </th>
              )}
              {baseHeaders.map((cellMetadata, columnIndex) => (
                <td key={`${cellMetadata.id}.${rowIndex + 1}`} className="p-0 align-bottom position-relative">
                  <GridCell
                    gridId={id}
                    isEditing={isEditing}
                    cellId={buildCellId(id, columnIndex, rowIndex)}
                    isSelected={isSelected(buildCellId(id, columnIndex, rowIndex))}
                    columnIndex={columnIndex}
                    rowIndex={rowIndex}
                    metadata={cellMetadata}
                    value={line?.[cellMetadata.id]}
                    onValueChanged={onCellValueChanged}
                    onPaste={onPaste}
                    onCellSelected={onCellSelected}
                    onNavigated={onNavigated}
                    setIsEditing={setIsEditing}
                    isDraggable={id === 'ScheduleDates'}
                    customizedData={valuesMetadata?.[rowIndex]?.[cellMetadata.id]}
                  />
                </td>
              ))}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
